import { leaguePath, teamPath } from '../../utils/siteUrls';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SportsScoreRoundedIcon from '@mui/icons-material/SportsScoreRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import api, { fetchLeagueData } from '../../services/api';
import { useCompetitions } from '../CompetitionContext';
import NavLink from '../common/NavLink';

const Widget = ({ title, icon, actionTo, actionLabel = 'Pełny ranking', minContentHeight = 140, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.6,
      border: '1px solid rgba(255,255,255,0.08)',
      bgcolor: 'background.surface',
      borderRadius: 2,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25} sx={{ mb: 1.2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        {icon}
        <Typography sx={{ fontWeight: 850 }}>{title}</Typography>
      </Stack>
      {actionTo && (
        <Typography
          component={NavLink}
          to={actionTo}
          sx={{
            color: 'accent.green',
            flexShrink: 0,
            fontSize: '0.72rem',
            fontWeight: 800,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {actionLabel}
        </Typography>
      )}
    </Stack>
    <Box sx={{ minHeight: minContentHeight }}>{children}</Box>
  </Paper>
);

const EmptyState = ({ children }) => (
  <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', py: 0.75 }}>
    {children}
  </Typography>
);

const getMatchDate = (match) => match.matchDate || match.match_date || match.date || null;
const getTeamName = (match, side) =>
  match?.[`${side}TeamName`] ||
  match?.[`${side}_team_name`] ||
  match?.[`${side}Name`] ||
  match?.[side]?.teamName ||
  match?.[side]?.name ||
  '';
const getTeamId = (match, side) =>
  match?.[`${side}TeamId`] ||
  match?.[`${side}_team_id`] ||
  match?.[side]?.teamId ||
  match?.[side]?.id ||
  null;
const getGoals = (match, side) =>
  match?.[`${side}Goals`] ??
  match?.[`${side}_goals`] ??
  match?.[side]?.goals ??
  null;

const formatMatchDate = (value) => {
  if (!value) return { date: '—', time: '—' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '—', time: '—' };
  return {
    date: date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }),
    time: date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }),
  };
};

const competitionDisplayName = (competition) => {
  if (!competition) return 'Liga';
  const group = (competition.group || '').trim();
  return group && group !== 'Brak' ? `${competition.name} (${group})` : competition.name;
};

const competitionUrl = (competition) =>
  competition ? leaguePath(competition.competition_id, competition) : '/';

const teamUrl = (teamId, teamName) =>
  teamId ? teamPath(teamId, teamName || '') : null;

const valueKeys = ['value', 'eloChange', 'elo_change', 'eloChangeSum', 'change', 'monthChange', 'score'];

const getAchievementValue = (row) => {
  const key = valueKeys.find((candidate) => row?.[candidate] != null);
  return key ? row[key] : null;
};

const AchievementMiniRanking = ({ category, tone = 'up' }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    api.get(`/achievements/${category}`, { params: { limit: 5 } })
      .then(({ data }) => {
        if (!active) return;
        setRows(data?.data || []);
      })
      .catch(() => {
        if (active) setRows([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [category]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rows.length) return <EmptyState>Brak danych do pokazania.</EmptyState>;

  return (
    <Stack spacing={0.65}>
      {rows.slice(0, 5).map((row, index) => {
        const value = getAchievementValue(row);
        const valueText = value == null ? '' : `${Number(value) > 0 ? '+' : ''}${value}`;

        return (
          <Stack key={`${category}-${row.teamId || row.competitionId || index}`} direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ width: 18, color: 'text.secondary', fontSize: '0.74rem' }}>{index + 1}</Typography>
            <Typography
              component={row.teamId ? NavLink : 'span'}
              to={row.teamId ? teamPath(row.teamId, row.teamName || '') : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                color: 'text.primary',
                textDecoration: 'none',
                fontSize: '0.82rem',
                fontWeight: 650,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': row.teamId ? { color: 'accent.green' } : undefined,
              }}
            >
              {row.teamName || row.leagueName || 'Drużyna'}
            </Typography>
            {valueText && (
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 850,
                  color: tone === 'up' ? 'accent.green' : '#ff8a80',
                }}
              >
                {valueText}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

const RightRail = () => {
  const { competitions, activeSeason } = useCompetitions();
  const [eloTeams, setEloTeams] = useState([]);
  const [eloLoading, setEloLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);

  const centralCompetitions = useMemo(() => {
    const all = Object.values(competitions || {}).flat();
    return all
      .filter((competition) => {
        const level = Number(competition.level);
        return competition.season === activeSeason &&
          competition.country === 'Poland' &&
          competition.type === 'league' &&
          level >= 1 &&
          level <= 4;
      })
      .sort((a, b) => Number(a.level) - Number(b.level) || (a.name || '').localeCompare(b.name || '', 'pl'));
  }, [competitions, activeSeason]);

  useEffect(() => {
    let active = true;
    setEloLoading(true);

    api.get('/elo-ranking', { params: { page: 0, size: 10 } })
      .then((response) => {
        if (!active) return;
        setEloTeams(response.data?.content || []);
      })
      .catch(() => {
        if (active) setEloTeams([]);
      })
      .finally(() => {
        if (active) setEloLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    Promise.allSettled(centralCompetitions.map((competition) => fetchLeagueData(competition.competition_id)))
      .then((results) => {
        if (!active) return;
        const now = new Date();
        const allMatches = results
          .flatMap((result, index) => {
            if (result.status !== 'fulfilled') return [];
            const competition = centralCompetitions[index];
            return (result.value?.matches || result.value?.fixtures || []).map((match) => ({ ...match, competition }));
          });

        const upcoming = allMatches
          .filter((match) => {
            const status = (match.status || '').toString().toLowerCase();
            const date = new Date(getMatchDate(match) || 0);
            return status === 'planned' && !Number.isNaN(date.getTime()) && date > now;
          })
          .sort((a, b) => new Date(getMatchDate(a) || 0) - new Date(getMatchDate(b) || 0))
          .slice(0, 6);

        const recent = allMatches
          .filter((match) => {
            const status = (match.status || '').toString().toLowerCase();
            const date = new Date(getMatchDate(match) || 0);
            return (status === 'ended' || status === 'finished') &&
              (!Number.isNaN(date.getTime()) ? date <= now : true);
          })
          .sort((a, b) => new Date(getMatchDate(b) || 0) - new Date(getMatchDate(a) || 0))
          .slice(0, 5);

        setMatches(upcoming);
        setRecentMatches(recent);
      })
      .catch(() => {
        if (active) {
          setMatches([]);
          setRecentMatches([]);
        }
      });

    return () => {
      active = false;
    };
  }, [centralCompetitions]);

  return (
    <Stack spacing={2} sx={{ position: 'sticky', top: 24 }}>
      <Widget title="Ostatnie mecze" minContentHeight={260} icon={<SportsScoreRoundedIcon sx={{ color: 'accent.cyan' }} />}>
        {recentMatches.length === 0 ? (
          <EmptyState>Brak ostatnich wyników w ligach centralnych.</EmptyState>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />}>
            {recentMatches.map((match, index) => {
              const date = formatMatchDate(getMatchDate(match));
              const homeName = getTeamName(match, 'home') || 'Gospodarze';
              const awayName = getTeamName(match, 'away') || 'Goście';
              const homeUrl = teamUrl(getTeamId(match, 'home'), homeName);
              const awayUrl = teamUrl(getTeamId(match, 'away'), awayName);
              const homeGoals = getGoals(match, 'home');
              const awayGoals = getGoals(match, 'away');

              return (
                <Box key={`recent-${getMatchDate(match)}-${index}`} sx={{ py: 0.65 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '38px 35px minmax(0, 1fr)', alignItems: 'center', columnGap: 0.8, mb: 0.2 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', minWidth: 38 }}>{date.date}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', minWidth: 35 }}>{date.time}</Typography>
                    <Chip
                      component={NavLink}
                      to={competitionUrl(match.competition)}
                      clickable
                      label={competitionDisplayName(match.competition)}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.64rem',
                        maxWidth: 180,
                        justifySelf: 'end',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 42px minmax(0, 1fr)',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.8rem',
                      lineHeight: 1.25,
                    }}
                  >
                    <Box
                      component={homeUrl ? NavLink : 'span'}
                      to={homeUrl || undefined}
                      sx={{
                        minWidth: 0,
                        color: 'inherit',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 650,
                        '&:hover': homeUrl ? { color: 'accent.green' } : undefined,
                      }}
                    >
                      {homeName}
                    </Box>
                    <Typography
                      sx={{
                        width: 42,
                        textAlign: 'center',
                        fontWeight: 900,
                        color: 'text.primary',
                        bgcolor: 'rgba(255,255,255,0.07)',
                        borderRadius: 1,
                        fontSize: '0.78rem',
                        py: 0.15,
                      }}
                    >
                      {homeGoals ?? '-'}:{awayGoals ?? '-'}
                    </Typography>
                    <Box
                      component={awayUrl ? NavLink : 'span'}
                      to={awayUrl || undefined}
                      sx={{
                        minWidth: 0,
                        color: 'inherit',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 650,
                        textAlign: 'right',
                        '&:hover': awayUrl ? { color: 'accent.green' } : undefined,
                      }}
                    >
                      {awayName}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Widget>

      <Widget title="Najbliższe mecze" minContentHeight={320} icon={<CalendarMonthRoundedIcon sx={{ color: 'accent.green' }} />}>
        {matches.length === 0 ? (
          <EmptyState>Brak najbliższych meczów w ligach centralnych.</EmptyState>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />}>
            {matches.map((match, index) => {
              const date = formatMatchDate(getMatchDate(match));
              const homeName = getTeamName(match, 'home') || 'Gospodarze';
              const awayName = getTeamName(match, 'away') || 'Goście';
              const homeUrl = teamUrl(getTeamId(match, 'home'), homeName);
              const awayUrl = teamUrl(getTeamId(match, 'away'), awayName);

              return (
                <Box key={`${getMatchDate(match)}-${index}`} sx={{ py: 0.65 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '38px 35px minmax(0, 1fr)', alignItems: 'center', columnGap: 0.8, mb: 0.2 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', minWidth: 38 }}>{date.date}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', minWidth: 35 }}>{date.time}</Typography>
                    <Chip
                      component={NavLink}
                      to={competitionUrl(match.competition)}
                      clickable
                      label={competitionDisplayName(match.competition)}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.64rem',
                        maxWidth: 160,
                        justifySelf: 'end',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 24px minmax(0, 1fr)',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.8rem',
                      lineHeight: 1.25,
                    }}
                  >
                    <Box
                      component={homeUrl ? NavLink : 'span'}
                      to={homeUrl || undefined}
                      sx={{
                        minWidth: 0,
                        color: 'inherit',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 650,
                        '&:hover': homeUrl ? { color: 'accent.green' } : undefined,
                      }}
                    >
                      {homeName}
                    </Box>
                    <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 800, fontSize: '0.8rem' }}>
                      -
                    </Typography>
                    <Box
                      component={awayUrl ? NavLink : 'span'}
                      to={awayUrl || undefined}
                      sx={{
                        minWidth: 0,
                        color: 'inherit',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 650,
                        textAlign: 'right',
                        '&:hover': awayUrl ? { color: 'accent.green' } : undefined,
                      }}
                    >
                      {awayName}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Widget>

      <Widget
        title="Ranking Elo"
        minContentHeight={240}
        icon={<EmojiEventsRoundedIcon sx={{ color: '#ffd54f' }} />}
        actionTo="/elo-ranking"
      >
        {eloLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={20} />
          </Box>
        ) : eloTeams.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>Ranking chwilowo niedostępny.</Alert>
        ) : (
          <Stack spacing={0.55}>
            {eloTeams.map((team, index) => (
              <Stack key={`${team.teamId}-${index}`} direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ width: 18, color: 'text.secondary', fontSize: '0.74rem' }}>{index + 1}</Typography>
                <Typography
                  component={NavLink}
                  to={teamPath(team.teamId, team.teamName)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: 'text.primary',
                    textDecoration: 'none',
                    fontSize: '0.82rem',
                    fontWeight: index < 3 ? 750 : 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: 'accent.green' },
                  }}
                >
                  {team.teamName}
                </Typography>
                <Typography sx={{ fontWeight: 800, color: index < 3 ? '#ffd54f' : 'text.secondary', fontSize: '0.78rem' }}>
                  {team.eloRanking}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Widget>

      <Widget
        title="Na topie"
        icon={<TrendingUpRoundedIcon sx={{ color: 'accent.green' }} />}
        actionTo="/achievements/biggest-elo-increase-month"
      >
        <AchievementMiniRanking category="biggest-elo-increase-month" tone="up" />
      </Widget>

      <Widget
        title="Dołują"
        icon={<TrendingDownRoundedIcon sx={{ color: '#ff8a80' }} />}
        actionTo="/achievements/biggest-elo-decrease-month"
      >
        <AchievementMiniRanking category="biggest-elo-decrease-month" tone="down" />
      </Widget>
    </Stack>
  );
};

export default RightRail;
